import { useEffect, useRef, type ReactNode } from 'react';

/*
 * Which drawers are open, innermost last.
 *
 * Escape closes the top one only. Every open drawer listens on the document, so
 * without this a drawer opened from inside another took the whole stack down
 * with it on one keypress -- and the thing a dispatcher wanted to go back to was
 * the drawer underneath.
 */
const openDrawers: symbol[] = [];

/**
 * Side drawer.
 *
 * Anything with more than a sentence of content opens here rather than in a
 * centred modal: it keeps the board visible behind it, so a dispatcher reading a
 * route detail can still see the rest of the fleet moving.
 */
export function Drawer({
  open,
  title,
  subtitle,
  onClose,
  footer,
  wide,
  stacked,
  modeless,
  children,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  footer?: ReactNode;
  /**
   * Roughly double width, for a drawer showing a map and a whole route rather
   * than a form. Opt-in per drawer: the narrow one is right for editing a
   * vehicle, and widening every drawer to suit one of them would be a worse
   * screen for the other six.
   */
  wide?: boolean;
  /**
   * Opened from inside another drawer.
   *
   * Lifts it and its scrim above the one behind, so the drawer underneath is
   * dimmed and out of reach rather than sitting at the same height and still
   * taking clicks.
   */
  stacked?: boolean;
  /**
   * No scrim: the screen behind stays bright and usable.
   *
   * For a drawer whose answer is drawn on the board behind it -- a fit
   * preview is read off the map, and dimming the map hid the very thing the
   * drawer had just put there. Escape and the close button still close it.
   */
  modeless?: boolean;
  children: ReactNode;
}) {
  /* Identity for the stack, stable across renders. */
  const id = useRef<symbol>(Symbol('drawer'));

  /*
   * `onClose` is usually an inline arrow, so it is a different function on every
   * render. Held in a ref and kept out of the dependencies below, because an
   * effect that re-ran on each render would pop and re-push this drawer and
   * quietly shuffle the order it is meant to be tracking.
   */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const self = id.current;
    openDrawers.push(self);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      /* Only the topmost answers, so Escape peels one layer at a time. */
      if (openDrawers[openDrawers.length - 1] !== self) return;
      closeRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      const at = openDrawers.indexOf(self);
      if (at >= 0) openDrawers.splice(at, 1);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!open) return null;

  return (
    <>
      {modeless ? null : (
        <div className="scrim" data-stacked={stacked ? 'true' : undefined} onClick={onClose} />
      )}
      <aside
        className="drawer"
        data-wide={wide ? 'true' : undefined}
        data-stacked={stacked ? 'true' : undefined}
        role="dialog"
        aria-modal={modeless ? undefined : 'true'}
        aria-label={title}
      >
        <header className="drawer-head">
          <div>
            <h2>{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path
                d="M2.5 2.5l7 7M9.5 2.5l-7 7"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div className="drawer-body">{children}</div>
        {footer ? <footer className="drawer-foot">{footer}</footer> : null}
      </aside>
    </>
  );
}
